<script>
import { SPECTRUM_PEAKS, buildSpectrumTexture } from '@/util/graphics/spectrum.js'

// The hero's figure: a simulated spectrum of a generated image with each
// upsampling peak circled. Static - drawn once, and again only on resize so
// the canvas stays sharp.
export default {
    name: 'SpectrumFigure',
    mounted() {
        this.texture = buildSpectrumTexture()
        this.resizeObserver = new ResizeObserver(() => this.draw())
        this.resizeObserver.observe(this.$refs.frame)
        this.draw()
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
    },
    methods: {
        draw() {
            const canvas = this.$refs.canvas
            const ratio = Math.min(window.devicePixelRatio || 1, 2)
            const size = Math.round(this.$refs.frame.clientWidth * ratio)
            if (!size) return
            canvas.width = size
            canvas.height = size

            const context = canvas.getContext('2d')
            const unit = size / 400
            context.imageSmoothingEnabled = true
            context.drawImage(this.texture, 0, 0, size, size)

            // Quarter and half-Nyquist guides
            context.strokeStyle = 'rgba(234, 244, 255, 0.08)'
            context.lineWidth = unit
            ;[0.25, 0.5, 0.75].forEach((fraction) => {
                context.beginPath()
                context.moveTo(size * fraction, 0)
                context.lineTo(size * fraction, size)
                context.moveTo(0, size * fraction)
                context.lineTo(size, size * fraction)
                context.stroke()
            })

            context.strokeStyle = 'rgba(234, 244, 255, 0.55)'
            context.lineWidth = 1.25 * unit
            SPECTRUM_PEAKS.forEach((peak) => {
                context.beginPath()
                context.arc(((peak.u + 1) / 2) * size, ((peak.v + 1) / 2) * size, 12 * unit, 0, Math.PI * 2)
                context.stroke()
            })
        },
    },
}
</script>

<template>
    <figure class="ps-spectrum mb-0">
        <div ref="frame" class="ps-spectrum__frame">
            <canvas ref="canvas" class="ps-spectrum__canvas" role="img" aria-label="A simulated frequency spectrum of a generated image, with a regular grid of bright peaks circled around the center."></canvas>
        </div>
        <figcaption class="ps-figcaption mt-3">
            <strong>Figure 1.</strong>
            Simulated log-magnitude Fourier spectrum of a generated image. The circled peaks form a regular lattice produced by the upsampling layers in many image generators. Spectra of camera captures fall off smoothly from the center and lack this lattice.
        </figcaption>
    </figure>
</template>

<style scoped lang="scss">
.ps-spectrum {
    position: relative;
}

// Soft bloom behind the plot, in the Sigma's colors
.ps-spectrum::before {
    content: '';
    position: absolute;
    top: 8%;
    left: 8%;
    width: 84%;
    padding-top: 84%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(81, 65, 255, 0.4), rgba(27, 154, 255, 0.1) 55%, transparent 72%);
    filter: blur(40px);
    pointer-events: none;
}

.ps-spectrum__frame {
    position: relative;
    aspect-ratio: 1 / 1;
    border: 1px solid var(--ps-rule);
    border-radius: 0.5rem;
    overflow: hidden;
    background: var(--ps-abyss);
}

.ps-spectrum__canvas {
    display: block;
    width: 100%;
    height: 100%;
}

.ps-spectrum .ps-figcaption {
    position: relative;
}
</style>
